/**
 * Progressive mermaid renderer.
 *
 * Mermaid's parser needs whole-line input — mid-line partials like
 * `graph T` (with no `D\n` yet) throw. Truncate to the last newline so
 * every completed line renders as deltas arrive, holding the last good
 * SVG between failed parses so the view doesn't blank. Mirrors mobile
 * _CodeBlockBuilder mermaid branch (message_bubble.dart:1057-1081).
 *
 * Theme follows the active CSS theme via document[data-theme]; backend
 * keeps mermaid source theme-neutral per 2026-05-08 contract.
 */
import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

let initialized = false;

const ensureInit = () => {
  if (initialized) return;
  const theme = document.documentElement?.getAttribute('data-theme') === 'light' ? 'default' : 'dark';
  mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme });
  initialized = true;
};

const MermaidBlock = ({ code }) => {
  const [svg, setSvg] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  const idRef = useRef(`mmd-${Math.random().toString(36).slice(2, 10)}`);
  const lastGoodRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    ensureInit();
    const lastNl = code.lastIndexOf('\n');
    const safe = lastNl > 0 ? code.slice(0, lastNl) : code;
    if (!safe.trim()) {
      setSvg(null);
      return () => {};
    }
    (async () => {
      try {
        const { svg: out } = await mermaid.render(idRef.current, safe);
        if (cancelled) return;
        lastGoodRef.current = out;
        setSvg(out);
        setErrMsg(null);
      } catch (e) {
        if (cancelled) return;
        if (lastGoodRef.current) {
          setSvg(lastGoodRef.current);
        } else {
          const msg = (e && (e.message || e.str || String(e))) || 'mermaid parse failed';
          setErrMsg(msg);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  if (errMsg && !svg) {
    return (
      <div className="not-prose my-2 rounded-lg overflow-hidden" style={{ border: '1px solid var(--emphasis-red)' }}>
        <div className="px-3 py-2 text-xs" style={{ background: 'var(--emphasis-red)', color: 'var(--on-emphasis)' }}>
          Mermaid syntax error — {errMsg.split('\n')[0].slice(0, 200)}
        </div>
        <pre className="p-3 overflow-x-auto text-xs" style={{ background: '#0d1117', color: '#e6edf3', margin: 0 }}>{code}</pre>
      </div>
    );
  }

  return (
    <div
      className="not-prose my-2 flex justify-center"
      style={{ minHeight: 40 }}
      dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
    />
  );
};

export default React.memo(MermaidBlock);
